/* =============================================================================
   What a 401 from /api/* means, and what the browser does about it.

   Identity issues a 15-minute access token. keepalive.ts now renews it every ten
   minutes for as long as somebody is working the screen, so this module handles
   what is left: the token lapsed anyway (the machine slept through its alarm,
   the renewal could not reach the IdP), or the session has genuinely ended.

   Before this module that failure was silent. The Customer Queue's poll swallows
   errors into its backoff, the badge poll keeps its previous counts, and the shop
   floor is left looking at a screen that has quietly stopped updating — until
   somebody tries to cancel an order and it just does not work. The terminal has
   to say so instead: send the browser through /auth/login, which is also what the
   PAGE gate does for an expired session (src/index.ts).

   That trip is invisible now, and it was not before. /auth/login starts the
   authorization-code flow against identity, which still holds its own session
   for this operator, so it redirects straight back through /auth/callback with a
   fresh token pair and lands on `next` — the same pane, no typing. It only did
   that AFTER an account-chooser page with a Continue button on it: identity's
   /oauth/authorize renders one to a signed-in user unless the request says
   `prompt=none` (its authorize.ts), and this redirect did not. Being made to
   press Continue in the middle of a sale, four times an hour, is the timeout
   people actually complained about. It says `prompt=none` now, and a silent
   attempt identity cannot satisfy comes back to /auth/callback as
   `error=login_required`, which asks again out loud.

   Not every surface: this is the credential of the staff terminals and the admin
   console. The mini-app is an anonymous shopper on a `bo_surface` cookie and the
   member app is a customer on `mk_member`; a 401 there means something else
   entirely, and bouncing a shopper into the staff IdP would be nonsense.
   ========================================================================== */
/** Surfaces whose credential is NOT an identity login — never send these to /auth/login. */
const NON_IDENTITY_PREFIXES = ["/miniapp", "/member", "/auth"];
/** Not part of the app, and a redirect from one would fight the page that owns it. */
export function currentPath() {
    try {
        return String(globalThis.location?.pathname ?? "");
    }
    catch {
        return "";
    }
}
/** Does this surface sign in with identity? Also read by keepalive.ts, which must not renew a
 *  session the mini-app shopper and the member app do not have. */
export function isIdentitySurface(path) {
    if (!path)
        return false;
    return !NON_IDENTITY_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
}
// One redirect per page load. The navigation tears this module down anyway; the flag matters for
// the burst BEFORE it lands — a terminal fires several /api calls per tick (queue poll + badge
// poll + whatever the pane is doing), and each one 401s, so without this they would all call
// location.assign() and stack navigations.
let redirecting = false;
/**
 * THE navigation into the auth flow — every caller on this page goes through here.
 *
 * The flag above only ever covered this module, and it is not the only thing on the page that can
 * decide to go and sign in: keepalive.ts navigates to /auth/login when a renewal is refused and
 * to /auth/idle at the ceiling, on its own clock. Two of those inside the same second is two
 * authorization flows, which is how brandos came to abandon half of every authorization code
 * identity issued it (see src/helpers/identity/attempts.ts for the count). The server no longer
 * breaks when it happens; this stops it happening.
 *
 * Returns false when a navigation is already under way — the caller has nothing left to do.
 */
export function startAuthNavigation(url) {
    if (redirecting)
        return false;
    const loc = globalThis.location;
    if (!loc || typeof loc.assign !== "function")
        return false;
    redirecting = true;
    loc.assign(url);
    return true;
}
/** Is this page already on its way to the IdP? Read by keepalive.ts before it acts on a clock. */
export function authNavigationStarted() {
    return redirecting;
}
/**
 * Test seam: back to "this page has not navigated yet".
 *
 * The flag is per page load and a real page load takes the module with it. A test file cannot do
 * that — keepalive.ts imports THIS module, so every cache-busted copy of keepalive shares one
 * copy of this flag, and case two would be standing on case one's navigation.
 */
export function __resetAuthNavigationForTest() {
    redirecting = false;
}
// Cross-page-load guard. If we come back from /auth/login and are STILL getting 401s — identity
// up but handing out a token this server won't accept, a clock skew, a revoked account — then
// redirecting again just spins the terminal through the IdP forever. Two loops inside this window
// and we stop trying and let the 401 surface as the plain error it is.
const LOOP_KEY = "ckn_reauth_at";
const LOOP_WINDOW_MS = 30_000;
function loopingSuspected(now) {
    try {
        const prev = Number(globalThis.sessionStorage?.getItem(LOOP_KEY) ?? 0);
        return Number.isFinite(prev) && prev > 0 && now - prev < LOOP_WINDOW_MS;
    }
    catch {
        return false;
    } // no sessionStorage → cannot detect a loop, so allow the redirect
}
function stampAttempt(now) {
    try {
        globalThis.sessionStorage?.setItem(LOOP_KEY, String(now));
    }
    catch { /* ignore */ }
}
/**
 * Handle a 401 from a same-origin API call: send this browser to re-authenticate, returning to
 * the page it is on. Safe to call for every failed request — it is a no-op for a non-401, for a
 * surface that does not sign in with identity, and for every call after the first.
 *
 * Returns true when a redirect was started, so a caller can stop rather than surface an error the
 * operator can do nothing about.
 */
export function handleAuthFailure(status) {
    if (status !== 401)
        return false;
    if (redirecting)
        return true;
    const path = currentPath();
    if (!isIdentitySurface(path))
        return false;
    const loc = globalThis.location;
    if (!loc || typeof loc.assign !== "function")
        return false;
    const now = Date.now();
    if (loopingSuspected(now))
        return false;
    stampAttempt(now);
    const next = String(loc.pathname ?? "/") + String(loc.search ?? "");
    // `prompt=none` — renew without putting anything in front of the operator. See the header:
    // without it identity shows its Continue-to-application page, and the whole trip stops being
    // invisible. /auth/callback turns the silent refusal into an ordinary interactive login.
    startAuthNavigation(`/auth/login?next=${encodeURIComponent(next)}&prompt=none`);
    return true;
}
/** Clears the loop guard — called once a request succeeds, so the window never goes stale. */
export function noteAuthSuccess() {
    try {
        globalThis.sessionStorage?.removeItem(LOOP_KEY);
    }
    catch { /* ignore */ }
}
//# sourceMappingURL=reauth.js.map